// feat_395 — Tablero CRM: estado de las columnas como funciones PURAS.
//
// Lo usan el fetch (carga / "ver más"), el drag & drop (movimiento optimista y
// su reversión) y los handlers de socket. Todo devuelve un array NUEVO de
// columnas: nunca muta el que recibe (el reducer de React depende de eso).
//
// Una columna = una entrada de `columns[]` de GET /crm/board + `key`:
//   key "none" = "Sin estado" (stateId null), si no String(stateId).
// Una ficha se identifica SIEMPRE por `contact.id` — un contacto está en una
// sola columna a la vez (su contactStateId).

export const NO_STATE_KEY = "none";

export const colKey = stateId =>
  stateId === null || stateId === undefined ? NO_STATE_KEY : String(stateId);

export const keyToStateId = key => (key === NO_STATE_KEY ? null : Number(key));

export const draggableIdFor = contactId => `contact-${contactId}`;

export const contactIdFromDraggable = draggableId => {
  const m = /^contact-(\d+)$/.exec(String(draggableId || ""));
  return m ? Number(m[1]) : null;
};

const activityTs = card => {
  const t = card && card.lastActivityAt ? Date.parse(card.lastActivityAt) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

// Mismo orden que el backend: lastActivityAt DESC, contact.id DESC.
export const compareCards = (a, b) => {
  const d = activityTs(b) - activityTs(a);
  if (d !== 0) return d;
  return (b.contact?.id || 0) - (a.contact?.id || 0);
};

export const normalizeColumns = columns =>
  (Array.isArray(columns) ? columns : []).map(c => ({
    ...c,
    key: colKey(c.stateId),
    cards: Array.isArray(c.cards) ? c.cards : [],
    total: Number(c.total) || 0,
  }));

export const findCard = (columns, contactId) => {
  for (let i = 0; i < columns.length; i += 1) {
    const j = columns[i].cards.findIndex(c => c.contact?.id === contactId);
    if (j >= 0) return { colIdx: i, cardIdx: j, card: columns[i].cards[j] };
  }
  return null;
};

const withHasMore = col => ({ ...col, hasMore: col.cards.length < col.total });

// "Ver más": agrega sin duplicar (un socket pudo haber metido alguna antes).
export const appendCards = (columns, key, page) =>
  columns.map(col => {
    if (col.key !== key) return col;
    const seen = new Set(col.cards.map(c => c.contact?.id));
    const extra = (page.cards || []).filter(c => !seen.has(c.contact?.id));
    const total = Number(page.total ?? col.total) || 0;
    return { ...col, cards: [...col.cards, ...extra], total, hasMore: !!page.hasMore };
  });

// Mueve una ficha CARGADA a otra columna. `toIndex` explícito (drag) o, si no
// viene, por orden de actividad (socket). Devuelve null si la ficha no está.
// Idempotente: si ya está en `toKey` no cambia nada (eco del propio drag).
export const moveCard = (columns, contactId, toKey, toIndex, patch) => {
  const found = findCard(columns, contactId);
  if (!found) return null;
  const from = columns[found.colIdx];
  if (from.key === toKey) return columns;
  const card = {
    ...found.card,
    ...(patch || {}),
    contactStateId: keyToStateId(toKey),
  };
  const targetExists = columns.some(c => c.key === toKey);
  return columns.map(col => {
    if (col.key === from.key) {
      const cards = col.cards.filter(c => c.contact?.id !== contactId);
      return withHasMore({ ...col, cards, total: Math.max(0, col.total - 1) });
    }
    if (targetExists && col.key === toKey) {
      const cards = [...col.cards];
      if (typeof toIndex === "number") {
        cards.splice(Math.max(0, Math.min(toIndex, cards.length)), 0, card);
      } else {
        cards.push(card);
        cards.sort(compareCards);
      }
      return withHasMore({ ...col, cards, total: col.total + 1 });
    }
    return col;
  });
};

// feat_397 — columna destino de un cambio de estado que llega por socket.
// Con etapa de entrada (`entryKey`), un contacto que queda SIN estado y tiene
// ticket vuelve a la columna de entrada (espejo del colKey del backend); sin
// ticket, a "Sin estado". Una ficha no cargada se resuelve igual que antes
// (moveCard devuelve null y el caller refresca totales).
export const targetKeyForState = (columns, contactId, stateId, entryKey) => {
  if (stateId !== null && stateId !== undefined) return colKey(stateId);
  if (!entryKey) return NO_STATE_KEY;
  const found = findCard(columns, contactId);
  if (found && !(found.card.ticket && found.card.ticket.id)) return NO_STATE_KEY;
  return entryKey;
};

// Saca una ficha cargada (dejó de cumplir un filtro).
export const removeCard = (columns, contactId) => {
  const found = findCard(columns, contactId);
  if (!found) return columns;
  return columns.map((col, i) =>
    i !== found.colIdx
      ? col
      : withHasMore({
          ...col,
          cards: col.cards.filter(c => c.contact?.id !== contactId),
          total: Math.max(0, col.total - 1),
        })
  );
};

export const patchCard = (columns, contactId, fn) => {
  const found = findCard(columns, contactId);
  if (!found) return columns;
  return columns.map((col, i) => {
    if (i !== found.colIdx) return col;
    const cards = col.cards.map(c => (c.contact?.id === contactId ? fn(c) : c));
    cards.sort(compareCards);
    return { ...col, cards };
  });
};

// Refresco liviano de totales (GET /crm/board?limit=1): conserva las fichas
// ya cargadas y toma de la respuesta fresca los totales y las columnas que
// aparecen/desaparecen (p. ej. una columna inactiva que quedó vacía).
export const mergeTotals = (columns, fresh) => {
  const freshCols = normalizeColumns(fresh);
  const byKey = new Map(columns.map(c => [c.key, c]));
  return freshCols.map(fc => {
    const cur = byKey.get(fc.key);
    if (!cur) return withHasMore(fc);
    return withHasMore({ ...cur, ...fc, cards: cur.cards, total: Math.max(fc.total, cur.cards.length) });
  });
};

// ── Criterios de pertenencia por filtro (espejo del WHERE de ListCrmBoardService)
// Los filtros NO dependen del estado: una ficha que los cumple los sigue
// cumpliendo en cualquier columna. Lo que sí cambia por socket es el ticket
// (operador) y la actividad (fecha) → esos dos ejes se re-evalúan acá.

export const userFilterActive = f => Array.isArray(f.userIds) && f.userIds.length > 0;

export const matchesUserFilter = (ticket, f) => {
  if (!userFilterActive(f)) return true;
  const uid = ticket && ticket.id ? ticket.userId ?? null : null;
  if (uid === null) return !!f.includeUnassigned;
  return f.userIds.includes(Number(uid));
};

const parseDay = (s, endOfDay) => {
  if (!s) return null;
  const d = new Date(`${s}T${endOfDay ? "23:59:59.999" : "00:00:00"}`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

export const matchesDateFilter = (lastActivityAt, f) => {
  const from = parseDay(f.lastContactFrom, false);
  const to = parseDay(f.lastContactTo, true);
  if (from === null && to === null) return true;
  const t = Date.parse(lastActivityAt);
  if (Number.isNaN(t)) return false;
  if (from !== null && t < from) return false;
  if (to !== null && t > to) return false;
  return true;
};

// feat_396 — filtro de colas del Funnel. `f.queue` = { showAll, queueIds,
// showOrphans, strict }. `strict` = el filtro achica de verdad lo que el
// usuario ve (admin con "Todos" apagado, o no-admin con alguna cola o "Sin
// asignar" destildados). Sin `strict` el backend no recorta por cola más allá
// del scope de la bandeja, así que el socket no debe sacar fichas por cola.
export const queueFilterActive = f => !!(f.queue && f.queue.strict);

export const matchesQueueFilter = (ticket, f) => {
  if (!queueFilterActive(f)) return true;
  const qid = ticket && ticket.id ? ticket.queueId ?? null : null;
  if (qid === null) return !!f.queue.showOrphans;
  return (f.queue.queueIds || []).includes(Number(qid));
};

// Parámetros de GET /crm/board a partir de los filtros de la UI.
export const buildBoardParams = (f, extra) => {
  const params = { ...(extra || {}) };
  if (f.queue) {
    if (f.queue.showAll) {
      params.showAll = "true";
    } else {
      params.queueIds = JSON.stringify(f.queue.queueIds || []);
      params.showOrphans = f.queue.showOrphans ? "true" : "false";
    }
  }
  if (f.hideChannels) params.hideChannels = "true";
  if (userFilterActive(f)) {
    params.userIds = JSON.stringify(f.userIds);
    if (f.includeUnassigned) params.includeUnassigned = "true";
  }
  if (f.lastContactFrom) params.lastContactFrom = f.lastContactFrom;
  if (f.lastContactTo) params.lastContactTo = f.lastContactTo;
  if (f.searchParam && f.searchParam.trim()) params.searchParam = f.searchParam.trim();
  return params;
};

export const hasAnyFilter = f =>
  userFilterActive(f) || queueFilterActive(f) || !!f.lastContactFrom || !!f.lastContactTo || !!(f.searchParam && f.searchParam.trim());
